(ns quuppa.qmd.steps.alignment
  (:require ["react" :as react]
            [re-frame.core :as rf]
            [quuppa.ui.icons :refer [icon image]]
            [quuppa.qmd.modal :as modal]
            [quuppa.qmd.map :as map]
            [quuppa.qmd.subs :as s]
            [quuppa.qmd.layout :as layout]
            [quuppa.qmd.steps.core :as core]
            [quuppa.qmd.map-utils :as map-utils]
            ["pure-react-carousel" :refer [CarouselProvider Slider Slide DotGroup CarouselContext]]))

(rf/reg-event-db ::start
  (fn [db _]
    (update db ::core/db assoc :state :choose-direction)))

(rf/reg-event-db ::confirm
  (fn [db _]
    (update db ::core/db assoc :state :set-orientation)))

(rf/reg-event-db ::save-orientation
  (fn [db [_ locator-xy [user-x user-y :as user-xy]]]
    (let [direction (-> db ::core/db :direction)
          azimuth (map-utils/get-azimuth direction locator-xy user-xy)]
      (update db ::core/db assoc
              :user {:x user-x
                     :y user-y}
              :azimuth-rad azimuth))))

(rf/reg-event-fx ::save
  (fn [{:keys [db]} [_ token locator]]
    (let [project-data (:orientation locator)
          deployment-data (get-in locator [:deployment-data :alignment :orientation])
          view-data (::core/db db)
          orientation {:azimuth-rad (or (:azimuth-rad view-data)
                                        (:azimuth-rad deployment-data)
                                        (:azimuth-rad project-data))
                       :user (or (:user view-data)
                                 (:user deployment-data))
                       :direction (or (:direction view-data)
                                      (:direction deployment-data))}]
      {:fx [[:server-dispatch {:event    [:deployment/set-alignment
                                          {:deployment-token token
                                           :locator-id       (:id locator)
                                           :orientation      orientation
                                           :done?            false}]
                               :on-success [::alignment-success]
                               :on-error [::stage-save-failed]}]]})))

(rf/reg-event-fx ::alignment-success
  (fn [{:keys [db]} _]
    {:db (update db ::core/db assoc :state :init)}))

(rf/reg-event-fx ::next-step
  (fn [_ [_ token locator-id]]
    {:fx [[:server-dispatch {:event      [:deployment/set-alignment
                                          {:deployment-token token
                                           :locator-id       locator-id
                                           :done?            true}]
                             :on-success [::to-verification]
                             :on-error   [::stage-save-failed]}]]}))

(rf/reg-event-fx ::to-verification
  (fn [_ _]
    {:fx [[:dispatch [::core/clear]]
          [:dispatch [:nav/update-query assoc :step "verification"]]]}))

(rf/reg-event-fx ::stage-save-failed
  (fn [{:keys [db]} [_ error]]
    {:db (update db ::core/db assoc
                 :state :error
                 :error error)
     :fx [[:dispatch [:notifications/notify
                      {:type       :wizard/stage-save-failed
                       :class      "alert-qmd"
                       :message    "Marking alignment stage completed failed."
                       :timeout-ms (* 10 1000)}]]]}))

(rf/reg-event-fx ::choose-direction
  (fn [{:keys [db]} [_ e]]
    (let [mouse-x (.-clientX e)
          mouse-y (.-clientY e)

          ^js img-el (.-target e)
          ^js img-rect (.getBoundingClientRect img-el)
          center-x (+ (.-x img-rect) (/ (.-width img-rect) 2))
          center-y (+ (.-y img-rect) (/ (.-height img-rect) 2))

          ;; coordinates for boxes are percents relative to the image height
          x (* 100 (/ (- mouse-x center-x) (.-height img-rect)))
          y (* 100 (/ (- mouse-y center-y) (.-height img-rect)))

          direction
          (cond
            (and (<= -8 y 8) (< -35 x -19)) :left
            (and (<= -8 y 8) (< 19 x 35)) :right
            (and (<= -8 x 8) (< 4 y 18)) :bottom
            (and (<= -8 x 8) (< -18 y -4)) :top
            :else nil)]
      ;; TODO: Maybe only update if we matched a direction, so we don't move backwards?
      {:db (update db ::core/db assoc
                   :direction direction
                   :state (if (some? direction) :confirm-direction :choose-direction))})))

;; When dragging user position on the map
(defn locator-orientation-start-move [click-state user-ref locator-ref disabled locator-xy direction e]
  (when-not disabled
    (let [;; Find the xy coordinate inside the locator icon
          ;; clicked/touched by the user, so we can use this
          ;; position as anchor.
          [x y] (map/client-xy e)
          rect (.getBoundingClientRect (.-current user-ref))
          dx (- (+ (.-x rect) (/ (.-width rect) 2)) x)
          dy (- (+ (.-y rect) (/ (.-height rect) 2)) y)]
      (reset! click-state {:down :orientation-move
                           :dx dx
                           :dy dy
                           :locator-xy locator-xy
                           :direction direction
                           ;; Store el so the move handler can modify transform directly.
                           :user-el (.-current user-ref)
                           :locator-el (.-current locator-ref)}))
    ;; Stop events propagation to the pan component.
    (.stopPropagation e)
    ;; Prevent touch moving the page
    (.preventDefault e)))

(defn map-mouse-move [svg-ref click-state e]
  (let [{:keys [down user-el locator-el locator-xy direction dx dy]} @click-state]
    (case down
      ;; Click started from map
      :orientation
      (swap! click-state update :movement +
             (js/Math.abs (.-movementX e))
             (js/Math.abs (.-movementY e)))

      ;; Move started from user element
      :orientation-move
      (let [[x y] (map/get-svg-xy svg-ref [dx dy] (map/client-xy e))
            azimuth (map-utils/get-azimuth direction locator-xy [x y])]
        ;; Update user position
        (.setAttribute user-el "transform" (map/entity-translate x y))
        ;; Update locator and line orientation
        (.setAttribute locator-el "transform" (str "translate(" (first locator-xy) ", " (second locator-xy) ") "
                                                   map/CS-TRANSFORM
                                                   " rotate(" (map-utils/quuppa-rad->css-deg azimuth) ")")))

      nil)))

(defn map-mouse-up [svg-ref click-state e]
  (let [{:keys [down dx dy movement]} @click-state]
    (when (or (= :orientation-move down)
              ;; If click on map container, check that we didn't pan the map
              (and (= :orientation down)
                   (< movement 15)))
      (let [xy (map/get-svg-xy svg-ref [dx dy] (map/client-xy e))]
        (rf/dispatch [::save-orientation (:locator-xy @click-state) xy])
        (swap! click-state assoc :down nil)))))

(defn locator-orientation [{:keys [locator disabled azimuth-rad xy direction]}]
  (let [;; Need to get placement location if pending
        locator-coords (:converted-location locator)
        {:keys [location]} (:placement (:deployment-data locator))
        locator-x (or (:x location) (:x locator-coords))
        locator-y (or (:y location) (:y locator-coords))

        [user-x user-y] xy

        ctx (react/useContext map/MapContext)
        scale (.-scale (:obj ctx))
        size (map/locator-size scale)

        locator-ref (react/useRef)
        pos-ref (react/useRef)
        ;; Need to directly add touch event listeners,
        ;; because the react pan lib is doing something strange,
        ;; and we need to attach this listener before those,
        ;; for stopPropagation to work.
        touch-start-f (react/useCallback (partial locator-orientation-start-move (:click-state ctx) pos-ref locator-ref disabled [locator-x locator-y] direction))
        pos-ref-f (fn [el]
                    (if el
                      (.addEventListener el "touchstart" touch-start-f)
                      (.removeEventListener (.-current pos-ref) "touchstart" touch-start-f))
                    (set! (.-current pos-ref) el))]
    [:g
     [:g
      {:ref locator-ref
       :transform (str (map/entity-translate locator-x locator-y)
                       " rotate(" (map-utils/quuppa-rad->css-deg azimuth-rad) ") ")}
      (when azimuth-rad
        [:g
         [:line
          {:x1 0 :y1 0.5 :x2 0 :y2 100
           :stroke-width (* 0.1 size)
           :stroke "white"
           :stroke-dasharray "0.1 0.2"}]
         [:line
          {:x1 0.5 :y1 0 :x2 100 :y2 0
           :stroke "white"
           :stroke-width (* 0.05 size)
           :stroke-dasharray "0.1 0.2"}]
         [:line
          {:x1 -0.5 :y1 0 :x2 -100 :y2 0
           :stroke "white"
           :stroke-width (* 0.05 size)
           :stroke-dasharray "0.1 0.2"}]
         [:line
          {:x1 0 :y1 -0.5 :x2 0 :y2 -100
           :stroke "#5A98F2"
           :stroke-width (* 0.1 size)
           :stroke-dasharray nil}]])
      [:g.qmd-alignment-locator
       [icon :quu-locator
        {:x (* -1 size)
         :y (* -1 size)
         :width (* 2 size)
         :height (* 2 size)}]]]

     (when xy
       [:g
        {:transform (map/entity-translate user-x user-y)
         :ref pos-ref-f
         ;; Move start here,
         ;; the mouse-move and mouse-up handlers are on the map-container el.
         :on-mouse-down (partial locator-orientation-start-move (:click-state ctx) pos-ref locator-ref disabled [locator-x locator-y] direction)}
        ;; TODO: Color
        [:rect
         {:x (* -1 size)
          :y (* -1 size)
          :width (* 2 size)
          :height (* 2 size)}]
        [icon :person-square.text-primary
         {:x (* -1 size)
          :y (* -1 size)
          :width (* 2 size)
          :height (* 2 size)}]])]))

(defn direction-text [direction]
  (case direction
    :bottom "The LED is pointing away from you"
    :left "The LED is pointing to your left"
    :right "The LED is pointing to your right"
    :top "The LED is pointing towards you"
    nil))

(defn alignment [{:keys [token cs-id locator-name step]}]
  (let [cs @(rf/subscribe [::s/coordinate-system token cs-id])
        locator @(rf/subscribe [::s/locator-by-name token cs-id locator-name])
        saved-orientation (:orientation (:alignment (:deployment-data locator)))
        locator-coords (:converted-location locator)
        {:keys [location]} (:placement (:deployment-data locator))
        x (or (:x location) (:x locator-coords))
        y (or (:y location) (:y locator-coords))
        {:keys [state direction user azimuth-rad error] :as view-data} @(rf/subscribe [::core/data])

        azimuth-rad (or azimuth-rad (:azimuth-rad saved-orientation))
        user (or user (:user saved-orientation))
        direction (or direction (:direction saved-orientation))

        already-set? (some? azimuth-rad)

        state (or state :init)
        disabled (not= :set-orientation state)]

    [:div.panel.qmd-wizard.d-flex.flex-column
     [:div.d-flex.flex-column.flex-grow-1.justify-content-between

      [:div.d-flex.flex-column.flex-grow-1
       (if-let [[text evt] (case state
                             (:choose-direction :confirm-direction) ["Select LED direction" [::core/clear-state]]
                             :set-orientation ["Tap on the map to set your position" [::core/clear]]
                             nil)]
         [layout/wizard-top-bar
          {:text text
           :on-close #(rf/dispatch evt)
           :help true}]
         [layout/wizard-navbar
          {:locator locator
           :token token
           :step step
           :on-back #(rf/dispatch [::core/close-wizard])
           :help true}])

       (when-let [i (case state
                      :init (when-not already-set?
                              (image "qmd_alignment_1"))
                      :choose-direction (image "qmd_alignment_2")
                      :confirm-direction (case direction
                                           :bottom (image "qmd_alignment_3_bottom")
                                           :left (image "qmd_alignment_3_left")
                                           :right (image "qmd_alignment_3_right")
                                           :top (image "qmd_alignment_3_top"))
                      nil)]
         [:div.flex-grow-1.d-flex.flex-column.justify-content-center
          [layout/qmd-image
           {:image i
            :on-click (fn [e]
                        (case state
                          (:choose-direction :confirm-direction)
                          (rf/dispatch [::choose-direction e])

                          nil))}]
          (when (= :init state)
            [:div.d-flex.flex-column.container.wizard-buttons
             [:button.btn.btn-primary.btn-lg.mt-4.align-self-center
              {:key "align"
               :on-click #(rf/dispatch [::start])}
              [icon :input-cursor.mr-3.text-white.icon-md]
              "Align locator"]])])

       (when (= :error state)
         [core/error-view error])

       (when (or (= :set-orientation state)
                 (and (= :init state) already-set?))
         [:div.d-flex.flex-column.flex-grow-1.position-relative
          [:div.w-100.d-flex.flex-row.justify-content-center.qmd-map-toolbar.py-3
           {:key "toobar"}
           [:button.btn.btn-dark.btn-lg
            {:key "edit"
             :on-click #(rf/dispatch [::start])}
            [icon :input-cursor.text-primary.icon-md]]]

          ^{:key "map"}
          [:f> map/map-container
           {:token            token
            :cs               cs
            :toolbar          false
            :selected-locator (:name locator)
            ;; When clicking anywhere on the map, if we don't have user coordinate yet,
            ;; place user with the first click.
            :on-mouse-down (fn [_svg-ref click-state _e]
                             (when-not disabled
                               (reset! click-state {:down :orientation
                                                    :movement 0
                                                    :locator-xy [x y]})))
            :on-mouse-move map-mouse-move
            :on-mouse-up map-mouse-up}

           [:g
            {:transform map/CS-TRANSFORM}
            (for [[_ l] (:locators cs)
                  :when (not= (:id locator) (:id l))]
              ^{:key (:id l)}
              [:f> map/locator-circle
               {:locator l
                :disabled true}])

            ^{:key "placement"}
            [:f> locator-orientation
             {:locator     locator
              :disabled    disabled
              :xy          (when user
                             [(:x user) (:y user)])
              :azimuth-rad azimuth-rad
              :direction   direction}]]]])

       (case state
         :init
         (when already-set?
           [:div.d-flex.flex-column.wizard-buttons.align-items-center.w-100.qmd-map-toolbar.qmd-map-toolbar--bottom
            {:key "toobar2"}
            [:button.btn.btn-primary.btn-lg
             {:key "next"
              :on-click #(rf/dispatch [::next-step token (:id locator)])}
             ;; Mark the stage done in backend & goto orientation
             "Next step"]])

         :error nil

         ;; Placeholders to match confirm text & button height.
         :choose-direction
         [:div.d-flex.flex-column.container.wizard-buttons.align-items-center
          [:<>
           [:p " "]
           [:div.button-placeholder.mt-4]]]

         :confirm-direction
         [:div.d-flex.flex-column.container.wizard-buttons.align-items-center
          [:<>
           [:p.text-center
            (direction-text direction)]
           [:button.btn.btn-outline-primary.btn-lg.mt-4
            {:key "continue"
             :on-click #(rf/dispatch [::confirm])}
            "Continue"]]]

         [:div.d-flex.flex-column.wizard-buttons.align-items-center.wizard-bottom-bar
          [:p.text-center.my-2
           (direction-text direction)]
          [:button.btn.btn-outline-primary.btn-lg.mt-3
           {:key "save"
            :on-click #(rf/dispatch [::save token locator])
            :disabled (not (:user view-data))}
           "Save changes"]])]]]))

(def help-pages
  [{:content [:p "Notice the 4 alignment marks on the Locator, one of which has the LED."]
    :img (image "qmd_alignment_help_1")}
   {:content [:p "Move to a position where you can see the marks aligned with your line of sight."]
    :img (image "qmd_alignment_help_2")}
   {:content [:p "Choose the direction in which the LED points."]
    :img (image "/qmd_alignment_help_3")}
   {:content [:p "Place your location on the map and check that the direction of the LED is correct."]
    ;; Map image in this screen is bitmap, so couldn't easily use svg.
    :img (image "qmd_alignment_help_4" "png")}])

(defn subscribe-active-page
  "Carousel active slide information is available through CarouselContext"
  [set-active-page]
  (let [ctx (react/useContext CarouselContext)]
    (react/useEffect (fn []
                       (let [on-change (fn [] (set-active-page (.. ctx -state -currentSlide)))]
                         (.subscribe ctx on-change)
                         (fn []
                           (.unsubscribe ctx on-change)))))
    nil))

(defn help []
  (let [[active-page set-active-page] (react/useState 0)]
    [modal/modal
     {:modal-class "qmd-help-modal"
      :content [:> CarouselProvider
                {:className "qmd-help-container"
                 :totalSlides (count help-pages)
                 :isIntrinsicHeight true}
                [:f> subscribe-active-page set-active-page]
                [:> DotGroup
                 {:className "qmd-help-pages-display"}]
                [:> Slider
                 {:className "qmd-help-slide"
                  :classNameTrayWrap "qmd-help-slide-tray-wrap"
                  :classNameTray "qmd-help-slide-tray"}
                 (for [[i {:keys [content img]}] (map-indexed vector help-pages)]
                   [:> Slide
                    {:key i
                     :index i
                     :className "qmd-help-page"
                     :innerClassName "qmd-help-page-inner"}
                    [:div.img.mb-3
                     [layout/qmd-image
                      {:image img}]]
                    [:div.content content]])]]
      :on-cancel #(rf/dispatch [:nav/update-query dissoc :help])
      :cancel-text false
      :on-ok #(rf/dispatch [:nav/update-query dissoc :help])
      :ok-class (if (= 3 active-page)
                  "btn-primary"
                  "btn-outline-primary")
      :ok-text "Ok, got it"}]))
